import React, { Suspense, lazy, memo, useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";

import { ReactComponent as CloseIcon } from "../../Assets/iconsv2/generic/closeBtn.svg";

import { useTranslate } from '@tolgee/react';
import { RotatingLines } from "react-loader-spinner";
import _, { cloneDeep } from "lodash";
import validator from "validator";

import OtherParticipant from "./OtherParticipant";
import { useAuthService } from "../../Context/AuthContext";

import PrimaryButton from "../GenericComponents/PrimaryButton";
import SearchMenu from "../GenericComponents/SearchMenu";
import { useNotify } from "../../Helpers/Notifications";

const FoundContactElement = lazy(() => import("../Chat/FoundContactElement"));

const AddMeetingMembers = ({
  contacts,
  handleSelectContact,
  handleInviteContact,
  selectedContacts,
  initialContacts,
  groupDetailsSide,
  handleBackBtn,
  handleSaveBtn,
  otherParticipants,
  setOtherParticipants,
  initialOtherParticipants,
}) => {
  const { user } = useAuthService();
  const theme = useTheme();
  const [confirmDisabled, setConfirmDisabled] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [participantEmail, setParticipantEmail] = useState("");
  const filteredOnAppContacts = contacts?.on_app_contacts.filter(
    (item) => item.id !== user.id
  );
  const { notifySuccess, notifyError, notifyInfo } = useNotify();

  const [invitedSnapshotIds] = useState(() => {
    const ids = new Set(selectedContacts.map((item) => item.id));
    if (groupDetailsSide) {
      initialContacts.forEach((item) => ids.add(item.id));
    }
    return ids;
  });

  const sortedOnAppContacts = [...filteredOnAppContacts].sort((a, b) => {
    const aInvited = invitedSnapshotIds.has(a.id);
    const bInvited = invitedSnapshotIds.has(b.id);
    if (aInvited === bInvited) return 0;
    return aInvited ? -1 : 1;
  });

  const handleAddOtherParticipant = (participant) => {
    let found = otherParticipants.find((item) => item === participant);
    if (found) {
      notifyError("Email already in the list");
    } else {
      let newParticipants = cloneDeep(otherParticipants);
      newParticipants.push(participant);
      setOtherParticipants(newParticipants);
      setParticipantEmail("");
    }
  };

  const ceckSelectedItem = (contact) => {
    let selected = false;

    if (groupDetailsSide) {
      let foundInSelected = selectedContacts.find(
        (element) => element.id === contact.id
      );
      let foundInInitial = initialContacts.find(
        (element) => element.id === contact.id
      );

      if (foundInSelected || foundInInitial) {
        selected = true;
      } else {
        selected = false;
      }
    } else {
      if (selectedContacts.find((item) => item.id === contact.id)) {
        selected = true;
      } else {
        selected = false;
      }
    }
    return selected;
  };

  const handleClickOtherParticipant = (participant) => {
    let filtered = otherParticipants.filter((item) => item !== participant);
    setOtherParticipants(filtered);
  };

  const searchMember = (list) => {
    if (searchInput === "") {
      return list;
    }
    let found = list.filter((item) =>
      item.name.toLowerCase().includes(searchInput?.toLowerCase())
    );
    return found;
  };
  const searchOtherMember = (list) => {
    if (searchInput === "") {
      return list;
    }
    let found = list.filter((item) =>
      item.toLowerCase().includes(searchInput?.toLowerCase())
    );
    return found;
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent form submission (if inside a form)
      if (validator.isEmail(participantEmail)) {
        handleAddOtherParticipant(participantEmail);
      }
    }
  };

  const checkChanges = () => {
    const contactChanges = _.isEqual(selectedContacts, initialContacts);
    const otherParticipant = _.isEqual(
      otherParticipants,
      initialOtherParticipants
    );

    if (contactChanges && otherParticipant) {
      setConfirmDisabled(true);
    } else {
      setConfirmDisabled(false);
    }
  };

  useEffect(() => {
    checkChanges();
  }, [selectedContacts, contacts, otherParticipants]);
  const { t } = useTranslate();
  return (
    <FullScreenItem>
      <Wrapper>
        <Header>
          <div
            style={{ width: "100%", display: "flex", justifyContent: "start" }}
          ></div>
          <ProfileTitle>
            <Title>{t('new_chat.select_participants')}</Title>
          </ProfileTitle>
          <div
            style={{ width: "100%", display: "flex", justifyContent: "end" }}
          >
            <CloseBtn>
              <CloseIcon onClick={handleBackBtn} />
            </CloseBtn>
          </div>
        </Header>
        <CardInputsWrapper>
          <Group style={{ overflow: "unset" }}>
            <Subtitle>
              {t('meetings.search_participant')}
            </Subtitle>
            <ChatOption style={{ flexDirection: "column" }}>
              <SearchMenu
                name="searchInput"
                value={searchInput}
                placeholder={t('general.search')}
                setvalue={setSearchInput}
              />
            </ChatOption>
          </Group>

          <Group style={{ overflow: "unset" }}>
            <Subtitle>
              {t('new_chat.add_participant_by_email')}
            </Subtitle>
            <ChatOption style={{ flexDirection: "column" }}>
              <SearchWrapper>
                <CustomInput
                  value={participantEmail}
                  placeholder={
                    t('new_chat.add_participant_email')
                  }
                  onChange={(e) => setParticipantEmail(e.currentTarget.value)}
                  onKeyDown={handleKeyDown}
                  name="participantEmail"
                  maxLength={100}
                />
                {validator.isEmail(participantEmail) && (
                  <AddEmailBtn
                    onClick={() => handleAddOtherParticipant(participantEmail)}
                  >
                    {t('general.add')}
                  </AddEmailBtn>
                )}
              </SearchWrapper>
            </ChatOption>
          </Group>

          <Group>
            <Subtitle>
              {t('meetings.selected_participants')}
            </Subtitle>
            <Suspense
              fallback={
                <LoaderWrapper>
                  <RotatingLines
                    strokeColor={theme.primaryButton.color}
                    width="50px"
                  />
                </LoaderWrapper>
              }
            >
              <OptionWrapper>
                {searchOtherMember(otherParticipants).map(
                  (otherParticipant, index) => (
                    <OtherParticipant
                      key={index}
                      item={otherParticipant}
                      selected={true}
                      handleSelectContact={handleClickOtherParticipant}
                    />
                  )
                )}
                {searchMember(sortedOnAppContacts).map((contact, index) => (
                  <FoundContactElement
                    key={contact.id}
                    item={contact}
                    handleSelectContact={handleSelectContact}
                    selected={ceckSelectedItem(contact)}
                  />
                ))}
              </OptionWrapper>
            </Suspense>
          </Group>
        </CardInputsWrapper>
        <PrimaryButton
          style={{ marginLeft: "auto" }}
          disabled={confirmDisabled}
          onClick={() => handleSaveBtn()}
        >
          {t('general.save')}
        </PrimaryButton>
      </Wrapper>
    </FullScreenItem>
  );
};

export default AddMeetingMembers;
const FullScreenItem = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 25px;
  height: 90%;
  width: 100%;
  max-width: 644px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  gap: 20px;
  border-radius: 4px;
`;

const CardInputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  overflow: auto;
  height: 100%;
`;

const Title = styled.p`
  text-transform: capitalize;
  font-weight: 700;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.04px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 12px;
  font-weight: 500;
  line-height: 22px;
  text-transform: uppercase;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Header = styled.div`
  gap: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const ProfileTitle = styled.div`
  width: 100%;
  justify-content: center;
  display: flex;
  min-width: 155px;
`;

const OptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 4px;
`;

const ChatOption = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;
  border-bottom: 1px solid #363e5180;
  &:last-child {
    border: none;
  }
  h1 {
    min-width: 75px;
    color: #ffffff99;
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: -0.40799999237060547px;
  }
  h2 {
    text-transform: capitalize;
    color: ${({ theme }) => theme.menu.primaryTextColor};
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: -0.40799999237060547px;
  }
`;

const CustomInput = styled.input`
  background: none;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-align: left;
  color: ${({ theme }) => theme.menu.primaryTextColor};

  width: 100%;
  border: none;
  &:focus {
    outline: none;
  }
  &::placeholder {
    font-weight: 400;
  }
`;
const LoaderWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
`;
const AddEmailBtn = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: ${({ theme }) => theme.menu.selectedTextColor};
  cursor: pointer;
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 8px;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 4px;
  gap: 8px;
  background: ${({ theme }) => theme.menu.background};
`;
const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  svg {
    height: 28px;
    width: 28px;
    color: ${({ theme }) => theme.menu.selectedOpionBackground};
    stroke: ${({ theme }) => theme.sidebar.borderColor};
  }
`;
